import React, { useState } from "react";
import { useAppSelector, useAppDispatch } from "../../../../app/hooks";
import {
  createDVIRQuestion,
  toggleShowAddQuestionModal,
} from "../../dvirSlice";
import { DVIRQuestionType } from "../../../../models";
import "./AddQuestionModal.scss";

export function AddQuestionModal() {
  const dispatch = useAppDispatch();
  const showModal = useAppSelector((state) => state.dvir.showAddQuestionModal);
  const createAPI = useAppSelector((state) => state.dvir.createDVIRQuestion);

  const [formData, setFormData] = useState({
    questionText: "",
    questionType: "boolean" as DVIRQuestionType,
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const resetForm = () => {
    setFormData({
      questionText: "",
      questionType: "boolean",
    });
    setErrors({});
  };

  React.useEffect(() => {
    if (!showModal) {
      resetForm();
    }
  }, [showModal]);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.questionText.trim()) {
      newErrors.questionText = "Question text is required";
    } else if (formData.questionText.trim().length < 5) {
      newErrors.questionText = "Question text must be at least 5 characters";
    } else if (formData.questionText.trim().length > 500) {
      newErrors.questionText = "Question text must be less than 500 characters";
    }

    if (!formData.questionType) {
      newErrors.questionType = "Question type is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    dispatch(
      createDVIRQuestion({
        questionText: formData.questionText.trim(),
        questionType: formData.questionType,
      })
    );
  };

  const handleClose = () => {
    resetForm();
    dispatch(toggleShowAddQuestionModal());
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && e.ctrlKey) {
      handleSubmit();
    } else if (e.key === "Escape") {
      handleClose();
    }
  };

  if (!showModal) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div
        className="add-question-modal"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        <div className="modal-header">
          <h3>Add New Question</h3>
          <button className="close-button" onClick={handleClose}>
            ×
          </button>
        </div>

        <div className="modal-form">
          <div className="form-group">
            <label htmlFor="questionText">Question Text *</label>
            <textarea
              id="questionText"
              value={formData.questionText}
              onChange={(e) =>
                handleInputChange("questionText", e.target.value)
              }
              placeholder="Enter the question text (e.g., 'Check engine oil level')"
              rows={3}
              className={errors.questionText ? "error" : ""}
            />
            {errors.questionText && (
              <span className="error-message">{errors.questionText}</span>
            )}
            <div className="character-count">
              {formData.questionText.length}/500 characters
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="questionType">Question Type *</label>
            <select
              id="questionType"
              value={formData.questionType}
              onChange={(e) =>
                handleInputChange(
                  "questionType",
                  e.target.value as DVIRQuestionType
                )
              }
              className={errors.questionType ? "error" : ""}
            >
              <option value="boolean">Boolean (Yes/No)</option>
              <option value="text">Text (Written response)</option>
              <option value="number">Number (Numeric input)</option>
            </select>
            {errors.questionType && (
              <span className="error-message">{errors.questionType}</span>
            )}
            <div className="help-text">
              {formData.questionType === "boolean" &&
                "Users will answer with Yes/No or Pass/Fail"}
              {formData.questionType === "text" &&
                "Users will provide a written response"}
              {formData.questionType === "number" &&
                "Users will enter a numeric value"}
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleClose}
            disabled={createAPI.loading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={createAPI.loading}
          >
            {createAPI.loading ? "Creating..." : "Create Question"}
          </button>
        </div>
      </div>
    </div>
  );
}
